<template>
  <div class="product-count-history">
    <div class="product-count-history__chart">
      <canvas
        ref="chartCanvas"
        class="product-count-history__canvas"
        role="img"
        aria-label="Historial de conteo de productos"
      ></canvas>

      <div v-if="loading && !hasData" class="product-count-history__state">
        <b-spinner small variant="primary" />
        <span>{{ $t("actions.loading") }}</span>
      </div>

      <div v-else-if="error && !hasData" class="product-count-history__state text-danger">
        <span>{{ error }}</span>
      </div>
    </div>

    <div v-if="error && hasData" class="product-count-history__notice text-warning">
      {{ error }}
    </div>
  </div>
</template>

<script>
import layoutMixin from "@/mixins/layoutMixin";
import { BSpinner } from "bootstrap-vue";
import Chart from "chart.js/auto";
import axios from "@axios";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

const REFRESH_INTERVAL = 5000;
const CHART_COLORS = [
  "#7367F0",
  "#28C76F",
  "#00CFE8",
  "#FF9F43",
  "#EA5455",
  "#A66DD4",
  "#4B8DF8",
  "#E83E8C",
];

export default {
  mixins: [layoutMixin],
  components: {
    BSpinner,
  },
  setup() {
    const chartCanvas = ref(null);
    const loading = ref(true);
    const error = ref("");
    const hasData = ref(false);
    const { skin } = useAppConfig();
    const isDark = computed(() => skin.value === "dark");

    let chartInstance = null;
    let refreshTimer = null;
    let isFetching = false;
    let chartData = { labels: [], datasets: [] };

    const getThemeColors = () => ({
      text: isDark.value ? "#D0D2D6" : "#5E5873",
      muted: isDark.value ? "#B4B7BD" : "#6E6B7B",
      grid: isDark.value ? "rgba(255, 255, 255, 0.08)" : "rgba(94, 88, 115, 0.10)",
      tooltip: isDark.value ? "#161D31" : "#FFFFFF",
    });

    const rgba = (hex, opacity) => {
      const value = hex.replace("#", "");
      const red = parseInt(value.substring(0, 2), 16);
      const green = parseInt(value.substring(2, 4), 16);
      const blue = parseInt(value.substring(4, 6), 16);

      return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
    };

    const normalizeData = (history) => {
      const entries = Array.isArray(history) ? history : [];
      const rows = entries.map((entry) => {
        const label = Object.keys(entry || {})[0];
        return {
          label,
          values: label ? entry[label] || {} : {},
        };
      }).filter(row => row.label);
      const productNames = Array.from(new Set(
        rows.reduce((names, row) => names.concat(Object.keys(row.values)), []),
      ));

      return {
        labels: rows.map(row => row.label),
        datasets: productNames.map((productName, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];

          return {
            label: productName,
            data: rows.map((row) => {
              const value = Number(row.values[productName]);
              return Number.isFinite(value) ? value : null;
            }),
            borderColor: color,
            backgroundColor: rgba(color, 0.12),
            borderWidth: 2.5,
            pointBackgroundColor: color,
            pointBorderColor: isDark.value ? "#283046" : "#FFFFFF",
            pointBorderWidth: 2,
            pointRadius: rows.length > 24 ? 0 : 2.5,
            pointHoverRadius: 5,
            pointHoverBorderWidth: 2,
            tension: 0.35,
            cubicInterpolationMode: "monotone",
            spanGaps: true,
            fill: false,
          };
        }),
      };
    };

    const getChartOptions = () => {
      const colors = getThemeColors();

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 450,
          easing: "easeOutQuart",
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        layout: {
          padding: {
            top: 4,
            right: 8,
            bottom: 2,
            left: 2,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: colors.muted,
              autoSkip: true,
              minRotation: 45,
              maxRotation: 45,
              padding: 8,
              font: { size: 11 },
            },
          },
          y: {
            beginAtZero: true,
            grace: "8%",
            grid: {
              color: colors.grid,
              drawBorder: false,
              borderDash: [4, 4],
            },
            ticks: {
              color: colors.muted,
              stepSize: 1,
              padding: 8,
              precision: 0,
              font: { size: 11 },
            },
          },
        },
        plugins: {
          legend: {
            position: "top",
            align: "start",
            labels: {
              color: colors.text,
              usePointStyle: true,
              pointStyle: "circle",
              boxWidth: 8,
              boxHeight: 8,
              padding: 18,
              font: {
                size: 12,
                weight: "500",
              },
            },
          },
          tooltip: {
            backgroundColor: colors.tooltip,
            titleColor: colors.text,
            bodyColor: colors.text,
            borderColor: colors.grid,
            borderWidth: 1,
            padding: 12,
            cornerRadius: 8,
            usePointStyle: true,
            displayColors: true,
            boxPadding: 5,
            callbacks: {
              label(context) {
                return ` ${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
      };
    };

    const createChart = async () => {
      await nextTick();
      if (!chartCanvas.value) return;

      chartInstance = new Chart(chartCanvas.value, {
        type: "line",
        data: chartData,
        options: getChartOptions(),
      });
    };

    const updateChart = () => {
      if (!chartInstance) {
        createChart();
        return;
      }

      chartInstance.data = chartData;
      chartInstance.options = getChartOptions();
      chartInstance.update();
    };

    const fetchData = async () => {
      if (isFetching) return;

      isFetching = true;
      if (!hasData.value) loading.value = true;
      error.value = "";

      try {
        const { data } = await axios.get("/v1/count_product/history");
        chartData = normalizeData(data);
        hasData.value = chartData.labels.length > 0 && chartData.datasets.length > 0;
        updateChart();
      } catch (requestError) {
        error.value = requestError?.response?.data?.message || "No fue posible cargar el historial";
      } finally {
        loading.value = false;
        isFetching = false;
      }
    };

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
    };

    watch(isDark, () => {
      chartData = {
        ...chartData,
        datasets: chartData.datasets.map((dataset) => ({
          ...dataset,
          pointBorderColor: isDark.value ? "#283046" : "#FFFFFF",
        })),
      };
      updateChart();
    });

    onMounted(async () => {
      await fetchData();
      refreshTimer = window.setInterval(fetchData, REFRESH_INTERVAL);
    });

    onBeforeUnmount(() => {
      window.clearInterval(refreshTimer);
      destroyChart();
    });

    return {
      chartCanvas,
      error,
      hasData,
      loading,
    };
  },
};
</script>

<style lang="scss" scoped>
.product-count-history {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 220px;
  padding: 0.75rem;
}

.product-count-history__chart {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.product-count-history__canvas {
  width: 100% !important;
  height: 100% !important;
}

.product-count-history__state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 1rem;
  text-align: center;
}

.product-count-history__notice {
  position: absolute;
  right: 0.75rem;
  bottom: 0.35rem;
  max-width: calc(100% - 1.5rem);
  overflow: hidden;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
